import React from 'react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { POSTURE_DOMAINS, OVERALL_POSTURE_SCORE, CONTROL_GAPS } from '../mitre-data';

const SCORE_COLORS = (s) => {
  if (s >= 80) return { color: '#34d399', label: 'Good' };
  if (s >= 65) return { color: '#eab308', label: 'Fair' };
  return { color: '#ef4444', label: 'At Risk' };
};

function ScoreRing({ score }) {
  const { color, label } = SCORE_COLORS(score);
  const r = 52, circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;

  return (
    <div className="posture-ring">
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
        <circle
          cx="65" cy="65" r={r}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 65 65)"
          style={{ filter: `drop-shadow(0 0 6px ${color}60)` }}
        />
        <text x="65" y="60" textAnchor="middle" fill="#e2e8f0" fontSize="26" fontWeight="700" fontFamily="'JetBrains Mono', monospace">{score}</text>
        <text x="65" y="78" textAnchor="middle" fill={color} fontSize="11" fontWeight="600">{label}</text>
      </svg>
      <p className="posture-ring__label">Overall Posture Score</p>
    </div>
  );
}

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = POSTURE_DOMAINS.find(x => x.name === payload[0]?.payload?.name);
    return (
      <div className="mitre-tooltip">
        <p className="mitre-tooltip__name">{payload[0].payload.name}</p>
        <div className="mitre-tooltip__row"><span>Score</span><strong>{payload[0].value}</strong></div>
        <div className="mitre-tooltip__row"><span>Benchmark</span><strong>{d?.benchmark}</strong></div>
        {d?.description && <p style={{fontSize:'0.7rem',color:'#94a3b8',margin:'4px 0 0'}}>{d.description}</p>}
      </div>
    );
  }
  return null;
};

const PRIORITY_COLOR = { Critical: '#ef4444', High: '#f97316', Medium: '#eab308', Low: '#34d399' };

export default function SecurityPostureScore() {
  const radarData = POSTURE_DOMAINS.map(d => ({
    name: d.name,
    score: d.score,
    benchmark: d.benchmark,
  }));

  return (
    <div className="security-posture">
      <div className="security-posture__header">
        <h3 className="security-posture__title">Security Posture</h3>
        <p className="security-posture__subtitle">Domain scores vs. industry benchmark</p>
      </div>

      <div className="security-posture__body">

        {/* Score ring */}
        <ScoreRing score={OVERALL_POSTURE_SCORE} />

        {/* Radar chart */}
        <div className="security-posture__radar">
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radarData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
              <PolarGrid stroke="rgba(255,255,255,0.08)" />
              <PolarAngleAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: '#64748b' }} tickCount={4} />
              <Tooltip content={<CustomTooltip />} />
              <Radar name="Benchmark" dataKey="benchmark" stroke="rgba(96,165,250,0.4)" fill="rgba(96,165,250,0.06)" strokeDasharray="4 3" />
              <Radar name="Score" dataKey="score" stroke="#34d399" fill="rgba(52,211,153,0.15)" strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="posture-radar-legend">
            <span><span className="posture-legend-line" style={{background:'#34d399'}}/>Your Score</span>
            <span><span className="posture-legend-line" style={{background:'rgba(96,165,250,0.6)', borderStyle:'dashed'}}/>Benchmark</span>
          </div>
        </div>

        {/* Domain bars */}
        <div className="security-posture__domains">
          {POSTURE_DOMAINS.map((d) => {
            const { color } = SCORE_COLORS(d.score);
            const delta = d.score - d.benchmark;
            const DeltaIcon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
            return (
              <div key={d.name} className="posture-domain">
                <div className="posture-domain__top">
                  <span className="posture-domain__name">{d.name}</span>
                  <span className="posture-domain__score" style={{ color }}>{d.score}</span>
                </div>
                <div className="posture-domain__bar-wrap">
                  <div className="posture-domain__bar" style={{ width: `${d.score}%`, background: color }} />
                  <div className="posture-domain__bench-marker" style={{ left: `${d.benchmark}%` }} />
                </div>
                <div className="posture-domain__meta">
                  <span className="posture-domain__delta" style={{ color: delta >= 0 ? '#34d399' : '#ef4444' }}>
                    <DeltaIcon size={10} /> {delta >= 0 ? '+' : ''}{delta} vs benchmark
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Control Gaps */}
      <div className="posture-gaps">
        <h4 className="posture-gaps__title">Identified Control Gaps</h4>
        <div className="posture-gaps__list">
          {CONTROL_GAPS.map((g, i) => (
            <div key={i} className="posture-gap-item">
              <span className="posture-gap-item__priority" style={{ color: PRIORITY_COLOR[g.priority], borderColor: `${PRIORITY_COLOR[g.priority]}40`, background: `${PRIORITY_COLOR[g.priority]}12` }}>{g.priority}</span>
              <div className="posture-gap-item__body">
                <span className="posture-gap-item__tactic">{g.tactic}</span>
                <span className="posture-gap-item__gap">{g.gap}</span>
              </div>
              <span className="posture-gap-item__effort">Effort: {g.effort}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
